<template>
  <div class="whatsapp-container">
    <div class="whatsapp-panels">
      <!-- Sidebar: canales -->
      <aside
        class="sidebar"
        v-show="!isMobile || !selectedChannel"
      >
        <div class="sidebar-top">
          <v-toolbar flat color="#f0f2f5" dense class="sidebar-header">
            <v-list-item-avatar color="primary" class="mr-3" size="36">
              <v-icon dark small>mdi-account-tie</v-icon>
            </v-list-item-avatar>
            <v-toolbar-title class="font-weight-medium sidebar-title">Chats</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openCreateChannelDialog">
                  <v-icon>mdi-plus-box-outline</v-icon>
                </v-btn>
              </template>
              <span>Crear Chat</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="fetchChannels" :loading="loadingChannels">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Actualizar Chats</span>
            </v-tooltip>
          </v-toolbar>
          <div class="sidebar-search pa-2">
            <v-text-field
              v-model="searchChannel"
              placeholder="Busca un chat..."
              outlined
              dense
              hide-details
              rounded
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              background-color="#f0f2f5"
            ></v-text-field>
          </div>
          <v-divider></v-divider>
        </div>
        <div class="channel-list-scroll">
          <v-list class="channel-list pa-0" v-if="filteredChannels.length > 0">
            <v-list-item
              v-for="channel in filteredChannels"
              :key="channel.id"
              @click="selectChannel(channel)"
              :class="{ 'active-channel': selectedChannel && selectedChannel.id === channel.id }"
              class="channel-item"
            >
              <v-list-item-avatar :color="getChannelColor(channel.name)">
                <span class="white--text font-weight-bold">{{ channel.name.charAt(0).toUpperCase() }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium d-flex justify-space-between align-center">
                  <span class="channel-name-text">{{ channel.name }}</span>
                  <span class="caption grey--text flex-shrink-0 ml-2" v-if="channel.updatedAt">{{ formatDate(channel.updatedAt) }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-truncate d-flex align-center">
                  <v-icon small color="grey" class="mr-1" v-if="channel.isClosed">mdi-lock</v-icon>
                  {{ channel.description || 'Sin descripción' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="text-center pa-5 grey--text">
            No se encontraron chats.
          </div>
        </div>
      </aside>

      <!-- Chat: mensajes -->
      <section
        class="chat-area"
        v-show="!isMobile || selectedChannel"
      >
        <template v-if="selectedChannel">
          <div class="chat-top">
            <v-toolbar flat color="#f0f2f5" dense class="chat-header">
              <v-btn icon class="d-md-none mr-1" @click="closeChannelMobile">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-list-item-avatar :color="getChannelColor(selectedChannel.name)" size="40">
                <span class="white--text font-weight-bold">{{ selectedChannel.name.charAt(0).toUpperCase() }}</span>
              </v-list-item-avatar>
              <v-toolbar-title class="font-weight-medium chat-header-title">
                {{ selectedChannel.name }}
                <div class="caption grey--text text-truncate chat-header-desc">
                  {{ selectedChannel.description }}
                </div>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" @click="openAddMemberDialog" color="primary">
                    <v-icon>mdi-account-plus</v-icon>
                  </v-btn>
                </template>
                <span>Agregar Miembro</span>
              </v-tooltip>
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="openEditChannelDialog">
                    <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Editar Chat</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="confirmDeleteChannel">
                    <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Eliminar Chat</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </div>

          <div
            class="chat-messages"
            ref="messagesContainer"
            @scroll="onMessagesScroll"
          >
            <div v-if="loadingMessages" class="text-center my-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <template v-else>
              <div v-if="loadingMoreMessages" class="text-center my-2">
                <v-progress-circular indeterminate color="primary" size="24" width="2"></v-progress-circular>
                <div class="caption grey--text mt-1">Cargando mensajes anteriores...</div>
              </div>
              <div v-else-if="hasMoreMessages" class="text-center my-2">
                <span class="caption grey--text bg-white-transparent px-3 py-1 rounded-lg d-inline-block">
                  Desplaza hacia arriba para ver más
                </span>
              </div>
              <div
                v-if="messages.length === 0"
                class="text-center my-4 grey--text bg-white-transparent pa-2 rounded-lg d-inline-block mx-auto align-self-center"
              >
                Aún no hay mensajes en este chat. Envía el primero.
              </div>
              <div
                v-for="group in groupedMessages"
                :key="group.key"
                class="message-date-group"
              >
                <div class="date-separator">
                  <span class="date-separator-label">{{ group.label }}</span>
                </div>
                <div
                  v-for="msg in group.messages"
                  :key="msg.id"
                  class="message-wrapper d-flex mb-3"
                  :class="isAdminMessage(msg) ? 'justify-end' : 'justify-start'"
                >
                  <div class="message-outer" :class="isMyMessage(msg) ? 'message-outer--mine' : 'message-outer--other'">
                    <div
                      class="message-card elevation-1 pa-2"
                      :class="getMessageClass(msg)"
                    >
                      <div class="d-flex align-center mb-1 flex-wrap" v-if="!isMyMessage(msg)">
                        <v-avatar size="20" :color="isAdminMessage(msg) ? 'deep-purple lighten-2' : 'teal lighten-2'" class="mr-1 flex-shrink-0">
                          <span style="font-size:10px; color:#fff; font-weight:bold;">
                            {{ getSenderInitial(msg) }}
                          </span>
                        </v-avatar>
                        <div class="sender-info">
                          <span class="caption font-weight-bold" :class="isAdminMessage(msg) ? 'deep-purple--text' : 'teal--text'">
                            {{ getSenderFullName(msg) }}
                          </span>
                          <v-chip
                            x-small
                            :color="isAdminMessage(msg) ? 'deep-purple lighten-5' : 'teal lighten-5'"
                            :text-color="isAdminMessage(msg) ? 'deep-purple darken-3' : 'teal darken-3'"
                            class="ml-1 sender-address-chip"
                          >
                            <v-icon style="font-size:9px" class="mr-1">{{ isAdminMessage(msg) ? 'mdi-shield-account' : 'mdi-home' }}</v-icon>
                            {{ getSenderAddress(msg) }}
                          </v-chip>
                        </div>
                      </div>
                      <div class="message-content text-body-1">{{ msg.content }}</div>

                      <!-- Badges de reacciones agrupados -->
                      <div class="reaction-badges" v-if="getReactionGroups(msg).length > 0">
                        <button
                          v-for="group in getReactionGroups(msg)"
                          :key="group.type"
                          class="reaction-badge"
                          :class="{ 'reaction-badge--mine': group.isMine }"
                          @click.stop="toggleReaction(msg, group.type)"
                          :title="group.isMine ? 'Quitar reacción' : 'Reaccionar con ' + group.emoji"
                        >
                          <span class="reaction-badge-emoji">{{ group.emoji }}</span>
                          <span class="reaction-badge-count">{{ group.count }}</span>
                        </button>
                      </div>

                      <div class="message-meta d-flex justify-end align-center mt-1">
                        <span class="caption grey--text mr-1">{{ formatTime(msg.createdAt) }}</span>
                        <div class="message-actions d-flex align-center">
                          <v-menu top :close-on-content-click="true">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                small
                                color="grey"
                                class="action-icon mx-1"
                                v-bind="attrs"
                                v-on="on"
                              >mdi-emoticon-outline</v-icon>
                            </template>
                            <v-card class="pa-1 d-flex">
                              <button
                                v-for="em in reactionEmojis"
                                :key="em.type"
                                class="reaction-picker-btn"
                                :class="{ 'reaction-picker-btn--active': getMyReaction(msg) && getMyReaction(msg).reactionType === em.type }"
                                @click.stop="toggleReaction(msg, em.type)"
                                :title="em.label"
                              >{{ em.emoji }}</button>
                            </v-card>
                          </v-menu>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                small
                                color="grey"
                                class="delete-icon"
                                v-bind="attrs"
                                v-on="on"
                                @click="deleteMessage(msg.id)"
                              >mdi-delete-outline</v-icon>
                            </template>
                            <span>Eliminar mensaje (Moderador)</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="chat-input-bar">
            <v-text-field
              v-model="newMessage"
              placeholder="Escribe un mensaje aquí..."
              outlined
              dense
              hide-details
              rounded
              class="mx-2 bg-white message-input-field"
              @keyup.enter="sendMessage"
              :loading="sendingMessage"
              :disabled="sendingMessage"
            ></v-text-field>
            <v-btn
              fab
              small
              depressed
              color="primary"
              class="mr-2 send-btn"
              @click="sendMessage"
              :disabled="!newMessage.trim() || sendingMessage"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-else>
          <div class="empty-chat-state d-flex flex-column align-center justify-center">
            <v-icon size="120" color="grey lighten-2">mdi-forum-outline</v-icon>
            <h2 class="grey--text text--darken-1 mt-4 font-weight-regular empty-chat-title">Selecciona un chat para comenzar</h2>
            <p class="grey--text text-center px-4">Gestiona los chats de comunicación de la unidad habitacional.</p>
          </div>
        </template>
      </section>
    </div>

    <!-- Dialog: Create/Edit Channel -->
    <v-dialog v-model="channelDialog" max-width="500px">
      <v-card class="rounded-xl">
        <v-card-title class="font-weight-bold">
          {{ isEditingChannel ? 'Editar chat' : 'Crear Nuevo chat' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="channelForm" v-model="channelFormValid" @submit.prevent="saveChannel">
            <v-text-field
              v-model="channelForm.name"
              label="Nombre del chat"
              outlined
              dense
              :rules="[v => !!v || 'El nombre es requerido']"
              class="mb-2"
            ></v-text-field>
            <v-textarea
              v-model="channelForm.description"
              label="Descripción"
              outlined
              dense
              rows="3"
              class="mb-2"
            ></v-textarea>
            <v-switch
              v-model="channelForm.isClosed"
              label="Chat cerrado (Solo administradores pueden publicar)"
              color="primary"
              hide-details
              class="mt-0"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="channelDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveChannel" :loading="savingChannel" :disabled="!channelFormValid">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog: Add Member -->
    <v-dialog v-model="addMemberDialog" max-width="520px" scrollable>
      <v-card class="rounded-xl member-dialog-card">
        <!-- Header estilo WhatsApp -->
        <div class="member-dialog-header">
          <div class="member-dialog-header-top">
            <v-btn icon class="mr-1" @click="addMemberDialog = false">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="member-dialog-title-wrap">
              <span class="member-dialog-title">Agregar Miembros</span>
              <span class="member-dialog-subtitle" v-if="selectedChannel">{{ selectedChannel.name }}</span>
            </div>
          </div>
          <!-- Búsqueda -->
          <div class="member-dialog-search">
            <v-text-field
              v-model="searchResident"
              placeholder="Buscar residente..."
              outlined
              dense
              hide-details
              rounded
              prepend-inner-icon="mdi-magnify"
              background-color="#f0f2f5"
              class="member-search-input"
              clearable
            ></v-text-field>
          </div>
          <!-- Chips de seleccionados -->
          <div class="member-selected-chips" v-if="selectedResidents.length > 0">
            <v-chip
              v-for="rid in selectedResidents"
              :key="rid"
              close
              small
              color="primary"
              class="mr-1 mb-1"
              @click:close="removeSelectedResident(rid)"
            >
              {{ getResidentName(rid) }}
            </v-chip>
          </div>
        </div>

        <!-- Filtros de estado -->
        <div class="member-filter-tabs">
          <button
            v-for="f in memberFilters"
            :key="f.value"
            class="member-filter-btn"
            :class="{ 'member-filter-btn--active': memberStatusFilter === f.value }"
            @click="memberStatusFilter = f.value"
          >
            {{ f.label }}
          </button>
        </div>

        <!-- Lista de residentes -->
        <v-card-text class="member-list-container pa-0">
          <div v-if="loadingResidents" class="member-loading">
            <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
            <span class="ml-3 grey--text">Cargando residentes...</span>
          </div>
          <div v-else-if="filteredResidentsByStatus.length === 0" class="member-empty">
            <v-icon size="48" color="grey lighten-2">mdi-account-off-outline</v-icon>
            <p class="grey--text mt-2 mb-0">No hay residentes en este filtro</p>
          </div>
          <div v-else class="member-list">
            <div
              v-for="resident in filteredResidentsByStatus"
              :key="resident.residentId"
              class="member-item"
              :class="{
                'member-item--selected': selectedResidents.includes(resident.residentId),
                'member-item--disabled': !isResidentSelectable(resident)
              }"
              @click="toggleResident(resident)"
            >
              <!-- Avatar -->
              <div class="member-item-avatar">
                <img
                  v-if="resident.avatar"
                  :src="resident.avatar"
                  :alt="resident.firstName"
                  class="member-avatar-img"
                />
                <div v-else class="member-avatar-fallback" :style="{ backgroundColor: getResidentColor(resident.firstName + resident.lastName) }">
                  <span>{{ (resident.firstName || '?').charAt(0).toUpperCase() }}</span>
                </div>
                <!-- Check overlay al seleccionar -->
                <div v-if="selectedResidents.includes(resident.residentId)" class="member-avatar-check">
                  <v-icon color="white" size="18">mdi-check</v-icon>
                </div>
              </div>
              <!-- Info -->
              <div class="member-item-info">
                <div class="member-item-name">{{ resident.firstName }} {{ resident.lastName }}</div>
                <div class="member-item-house">
                  <v-icon size="12" color="grey">mdi-home-outline</v-icon>
                  <span>{{ resident.house || 'Sin dirección' }}</span>
                </div>
              </div>
              <!-- Badge de estado -->
              <div class="member-item-status">
                <v-chip
                  x-small
                  :color="getMembershipStatusColor(resident.membershipStatus)"
                  :text-color="getMembershipStatusTextColor(resident.membershipStatus)"
                  class="membership-badge"
                >
                  <v-icon x-small class="mr-1">{{ getMembershipStatusIcon(resident.membershipStatus) }}</v-icon>
                  {{ getMembershipStatusLabel(resident.membershipStatus) }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Footer con acción -->
        <div class="member-dialog-footer">
          <div class="member-footer-info">
            <span v-if="selectedResidents.length > 0" class="caption primary--text font-weight-medium">
              {{ selectedResidents.length }} seleccionado{{ selectedResidents.length !== 1 ? 's' : '' }}
            </span>
            <span v-else class="caption grey--text">Selecciona residentes para invitar</span>
          </div>
          <div class="member-footer-actions">
            <v-btn text color="grey darken-1" @click="addMemberDialog = false" :disabled="addingMember">Cancelar</v-btn>
            <v-btn
              color="primary"
              depressed
              rounded
              @click="addMember"
              :loading="addingMember"
              :disabled="selectedResidents.length === 0"
              class="member-add-btn"
            >
              <v-icon left small>mdi-account-plus</v-icon>
              Invitar
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import store from '@/store' // Assuming vuex store is available for auth

export default {
  name: 'CommunicationChannelAdmin',
  data () {
    return {
      channels: [],
      loadingChannels: false,
      searchChannel: '',
      selectedChannel: null,
      messages: [],
      messagePollingTimer: null,
      pollingMessages: false,
      loadingMessages: false,
      loadingMoreMessages: false,
      currentPage: 1,
      pageLimit: 2,
      hasMoreMessages: false,
      messagesCount: 0,
      newMessage: '',
      sendingMessage: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      // Dialogs
      channelDialog: false,
      isEditingChannel: false,
      channelFormValid: false,
      savingChannel: false,
      channelForm: {
        id: null,
        name: '',
        description: '',
        isClosed: false
      },
      addMemberDialog: false,
      residentsMembershipStatus: [],
      loadingResidents: false,
      selectedResidents: [],
      searchResident: '',
      memberStatusFilter: 'all',
      memberFilters: [
        { label: 'Todos', value: 'all' },
        { label: 'No invitados', value: 'not_member' },
        { label: 'Pendiente', value: 'pending' },
        { label: 'Miembro', value: 'accepted' },
        { label: 'Rechazado', value: 'rejected' }
      ],
      addingMember: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      },
      // Colors for avatars
      avatarColors: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ff9800', '#ff5722', '#795548', '#607d8b'],
      // Reacciones disponibles
      reactionEmojis: [
        { type: 'like', emoji: '👍', label: 'Me gusta' },
        { type: 'dislike', emoji: '👎', label: 'No me gusta' }
      ]
    }
  },
  computed: {
    filteredChannels () {
      if (!this.searchChannel) return this.channels
      const term = this.searchChannel.toLowerCase()
      return this.channels.filter(c => c.name.toLowerCase().includes(term) || (c.description && c.description.toLowerCase().includes(term)))
    },
    currentUser () {
      // Assuming store has user info
      return store.state.user || { id: 1 } // Fallback
    },
    isMobile () {
      return this.windowWidth < 960
    },
    groupedMessages () {
      if (!this.messages.length) return []
      const groups = []
      let currentGroup = null
      this.messages.forEach(msg => {
        const label = this.getDateGroupLabel(msg.createdAt)
        const key = this.getDateGroupKey(msg.createdAt)
        if (!currentGroup || currentGroup.key !== key) {
          currentGroup = { key, label, messages: [] }
          groups.push(currentGroup)
        }
        currentGroup.messages.push(msg)
      })
      return groups
    },
    filteredResidentsByStatus () {
      let list = this.residentsMembershipStatus
      // Filtro por estado
      if (this.memberStatusFilter !== 'all') {
        list = list.filter(r => r.membershipStatus === this.memberStatusFilter)
      }
      // Filtro por búsqueda de texto
      if (this.searchResident && this.searchResident.trim()) {
        const term = this.searchResident.trim().toLowerCase()
        list = list.filter(r => {
          const fullName = `${r.firstName || ''} ${r.lastName || ''}`.toLowerCase()
          const house = (r.house || '').toLowerCase()
          const email = (r.email || '').toLowerCase()
          return fullName.includes(term) || house.includes(term) || email.includes(term)
        })
      }
      return list
    }
  },
  mounted () {
    this.fetchChannels()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    this.stopMessagePolling()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.windowWidth = window.innerWidth
    },
    closeChannelMobile () {
      this.stopMessagePolling()
      this.selectedChannel = null
      this.resetPagination()
    },
    resetPagination () {
      this.messages = []
      this.currentPage = 1
      this.hasMoreMessages = false
      this.messagesCount = 0
      this.loadingMessages = false
      this.loadingMoreMessages = false
    },
    getApiHeaders () {
      const token = localStorage.getItem('token') || store.getters.getToken
      return {
        'Authorization': `token  ${token}`,
        'Content-Type': 'application/json'
      }
    },
    showNotification (text, color = 'success') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    },
    getChannelColor (name) {
      if (!name) return this.avatarColors[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      const index = Math.abs(hash) % this.avatarColors.length
      return this.avatarColors[index]
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString()
    },
    formatTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    startOfDay (date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    },
    getDateGroupKey (dateStr) {
      if (!dateStr) return 'unknown'
      const msgDate = this.startOfDay(dateStr)
      const today = this.startOfDay(new Date())
      const diffDays = Math.round((today.getTime() - msgDate.getTime()) / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'hoy'
      if (diffDays === 1) return 'ayer'
      if (diffDays >= 2 && diffDays < 7) return 'esta-semana'
      if (diffDays >= 7 && diffDays < 14) return 'semana-pasada'
      if (diffDays >= 14 && diffDays < 30) return 'este-mes'
      return `mes-${msgDate.getFullYear()}-${msgDate.getMonth()}`
    },
    getDateGroupLabel (dateStr) {
      if (!dateStr) return 'Sin fecha'
      const key = this.getDateGroupKey(dateStr)
      const labels = {
        hoy: 'Hoy',
        ayer: 'Ayer',
        'esta-semana': 'Esta semana',
        'semana-pasada': 'La semana pasada',
        'este-mes': 'Este mes'
      }
      if (labels[key]) return labels[key]

      const msgDate = this.startOfDay(dateStr)
      const label = msgDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    async fetchChannels () {
      this.loadingChannels = true
      try {
        const res = await axios.get('https://residencial-services.intrare.app/channels/', { headers: this.getApiHeaders() })
        this.channels = res.data
      } catch (err) {
        console.error('Error fetching channels', err)
        // Fallback mock data for UI demo purposes if API is not fully ready
        this.channels = [
          { id: '1', name: 'Anuncios Generales', description: 'Chat para avisos importantes.', isClosed: true, updatedAt: new Date().toISOString() },
          { id: '2', name: 'Comunidad', description: 'Chat general para residentes.', isClosed: false, updatedAt: new Date().toISOString() }
        ]
      } finally {
        this.loadingChannels = false
      }
    },
    selectChannel (channel) {
      this.stopMessagePolling()
      this.selectedChannel = channel
      this.resetPagination()
      this.fetchMessages(channel.id, true)
      this.startMessagePolling(channel.id)
    },
    startMessagePolling (channelId) {
      this.stopMessagePolling()
      this.messagePollingTimer = setInterval(() => {
        this.fetchLatestMessages(channelId)
      }, 5000)
    },
    stopMessagePolling () {
      if (this.messagePollingTimer !== null) {
        clearInterval(this.messagePollingTimer)
        this.messagePollingTimer = null
      }
    },
    async fetchMessages (channelId, reset = false) {
      if (reset) {
        this.loadingMessages = true
        this.currentPage = 1
        this.messages = []
        this.hasMoreMessages = false
      } else {
        if (this.loadingMoreMessages || !this.hasMoreMessages) return
        this.loadingMoreMessages = true
      }

      try {
        const page = reset ? 1 : this.currentPage + 1
        const res = await axios.get(
          `https://residencial-services.intrare.app/channel-messages/paginated/?idChannel=${channelId}&page=${page}&limit=${this.pageLimit}`,
          { headers: this.getApiHeaders() }
        )
        const payload = res.data || {}
        const results = Array.isArray(payload.results) ? payload.results : []
        // API returns newest → oldest; reverse for chronological chat display
        const chronological = results.slice().reverse()
        const existingIds = new Set(this.messages.map(m => m.id))
        const uniqueNew = chronological.filter(m => !existingIds.has(m.id))

        if (reset) {
          this.messages = uniqueNew
          this.currentPage = 1
          this.messagesCount = payload.count || this.messages.length
          this.hasMoreMessages = !!payload.next
          this.scrollToBottom()
          this.$nextTick(() => {
            this.fillViewportIfNeeded(channelId)
          })
        } else {
          const container = this.$refs.messagesContainer
          const prevScrollHeight = container ? container.scrollHeight : 0
          this.messages = [...uniqueNew, ...this.messages]
          this.currentPage = page
          this.messagesCount = payload.count || this.messages.length
          this.hasMoreMessages = !!payload.next
          this.$nextTick(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - prevScrollHeight
            }
            this.fillViewportIfNeeded(channelId)
          })
        }
      } catch (err) {
        console.error('Error fetching messages', err)
        if (reset) {
          this.messages = []
          this.hasMoreMessages = false
        }
        // this.showNotification('No se pudieron cargar los mensajes', 'error')
      } finally {
        this.loadingMessages = false
        this.loadingMoreMessages = false
      }
    },
    async fetchLatestMessages (channelId) {
      if (
        this.pollingMessages ||
        this.loadingMessages ||
        !this.selectedChannel ||
        String(this.selectedChannel.id) !== String(channelId)
      ) return

      this.pollingMessages = true
      try {
        const res = await axios.get(
          `https://residencial-services.intrare.app/channel-messages/paginated/?idChannel=${channelId}&page=1&limit=20`,
          { headers: this.getApiHeaders() }
        )
        if (!this.selectedChannel || String(this.selectedChannel.id) !== String(channelId)) return

        const payload = res.data || {}
        const results = Array.isArray(payload.results) ? payload.results : []
        const latestMessages = results.slice().reverse()
        const latestById = new Map(latestMessages.map(message => [String(message.id), message]))
        const existingIds = new Set(this.messages.map(message => String(message.id)))
        const hasNewMessages = latestMessages.some(message => !existingIds.has(String(message.id)))
        const container = this.$refs.messagesContainer
        const wasAtBottom = container &&
          container.scrollHeight - container.scrollTop - container.clientHeight <= 80

        const mergedMessages = this.messages.map(message => latestById.get(String(message.id)) || message)
        latestMessages.forEach(message => {
          if (!existingIds.has(String(message.id))) mergedMessages.push(message)
        })
        mergedMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        this.messages = mergedMessages

        if (payload.count !== undefined && payload.count !== null) {
          this.messagesCount = payload.count
        }
        this.hasMoreMessages = !!payload.next
        if (hasNewMessages && wasAtBottom) this.scrollToBottom()
      } catch (err) {
        console.error('Error polling messages', err)
      } finally {
        this.pollingMessages = false
      }
    },
    fillViewportIfNeeded (channelId) {
      const container = this.$refs.messagesContainer
      if (
        container &&
        this.hasMoreMessages &&
        !this.loadingMoreMessages &&
        !this.loadingMessages &&
        container.scrollHeight <= container.clientHeight + 20
      ) {
        this.fetchMessages(channelId, false)
      }
    },
    onMessagesScroll (event) {
      const container = event.target
      if (!container || !this.selectedChannel) return
      // Load older messages when scrolling near the top
      if (
        container.scrollTop <= 80 &&
        this.hasMoreMessages &&
        !this.loadingMoreMessages &&
        !this.loadingMessages
      ) {
        this.fetchMessages(this.selectedChannel.id, false)
      }
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    isMyMessage (msg) {
      // Use senderData.id (UUID string) if present; fallback to sender int id
      if (msg.senderData && msg.senderData.id) {
        return String(msg.senderData.id) === String(this.currentUser.id)
      }
      return msg.sender === this.currentUser.id
    },
    isAdminMessage (msg) {
      // The backend sets address = 'Administración' for admin senders
      return msg.senderData && msg.senderData.address === 'Administración'
    },
    getSenderFullName (msg) {
      if (msg.senderData) {
        return `${msg.senderData.first_name || ''} ${msg.senderData.last_name || ''}`.trim() || 'Sin nombre'
      }
      return msg.senderName || 'Desconocido'
    },
    getSenderInitial (msg) {
      const name = this.getSenderFullName(msg)
      return name.charAt(0).toUpperCase()
    },
    getSenderAddress (msg) {
      return (msg.senderData && msg.senderData.address) ? msg.senderData.address : 'Sin dirección'
    },
    getMessageClass (msg) {
      if (this.isMyMessage(msg)) return 'message-mine'
      if (this.isAdminMessage(msg)) return 'message-admin'
      return 'message-resident'
    },
    async sendMessage () {
      if (!this.newMessage.trim() || !this.selectedChannel) return
      this.sendingMessage = true
      try {
        const payload = {
          idChannel: this.selectedChannel.id,
          content: this.newMessage
        }
        const res = await axios.post('https://residencial-services.intrare.app/channel-messages/', payload, { headers: this.getApiHeaders() })
        this.messages.push(res.data)
        this.messagesCount += 1
        this.newMessage = ''
        this.scrollToBottom()
      } catch (err) {
        console.error('Error sending message', err)
        // Mock successful send for UI demo
        this.messages.push({
          id: Date.now().toString(),
          idChannel: this.selectedChannel.id,
          content: this.newMessage,
          sender: this.currentUser.id,
          senderData: {
            id: String(this.currentUser.id),
            first_name: 'Administrador',
            last_name: '',
            email: '',
            avatar: null,
            address: 'Administración'
          },
          reactions: [],
          createdAt: new Date().toISOString()
        })
        this.messagesCount += 1
        this.newMessage = ''
        this.scrollToBottom()
      } finally {
        this.sendingMessage = false
      }
    },
    async deleteMessage (messageId) {
      if (!confirm('¿Estás seguro de que deseas eliminar este mensaje?')) return
      try {
        await axios.delete(`https://residencial-services.intrare.app/channel-messages/${messageId}/`, { headers: this.getApiHeaders() })
        this.messages = this.messages.filter(m => m.id !== messageId)
        this.messagesCount = Math.max(0, this.messagesCount - 1)
        this.showNotification('Mensaje eliminado')
      } catch (err) {
        console.error('Error deleting message', err)
        // Mock successful delete
        this.messages = this.messages.filter(m => m.id !== messageId)
        this.messagesCount = Math.max(0, this.messagesCount - 1)
        this.showNotification('Mensaje eliminado')
      }
    },
    // --- Reactions ---
    getMyReaction (msg) {
      if (!msg.reactions || !msg.reactions.length) return null
      const userId = String(this.currentUser.id)
      return msg.reactions.find(r => String(r.idUser) === userId) || null
    },
    getReactionGroups (msg) {
      if (!msg.reactions || !msg.reactions.length) return []
      const userId = String(this.currentUser.id)
      const counts = {}
      msg.reactions.forEach(r => {
        if (!counts[r.reactionType]) {
          counts[r.reactionType] = { type: r.reactionType, count: 0, isMine: false }
        }
        counts[r.reactionType].count++
        if (String(r.idUser) === userId) {
          counts[r.reactionType].isMine = true
        }
      })
      return Object.values(counts).map(g => {
        const found = this.reactionEmojis.find(e => e.type === g.type)
        return {
          ...g,
          emoji: found ? found.emoji : '❓'
        }
      })
    },
    async toggleReaction (msg, type) {
      const existing = this.getMyReaction(msg)
      if (existing) {
        if (existing.reactionType === type) {
          // Misma reacción → eliminar
          await this.deleteReaction(msg, existing)
        } else {
          // Distinta reacción → cambiar (upsert)
          await this.upsertReaction(msg, type, existing)
        }
      } else {
        // Sin reacción propia → crear
        await this.upsertReaction(msg, type, null)
      }
    },
    async upsertReaction (msg, type, existing) {
      const userId = String(this.currentUser.id)
      // Actualización optimista
      if (!msg.reactions) this.$set(msg, 'reactions', [])
      if (existing) {
        const idx = msg.reactions.indexOf(existing)
        if (idx !== -1) {
          this.$set(msg.reactions, idx, { ...existing, reactionType: type })
        }
      } else {
        msg.reactions.push({ reactionType: type, idUser: userId, id: null })
      }
      try {
        const payload = { idMessage: msg.id, reactionType: type }
        const res = await axios.post(
          'https://residencial-services.intrare.app/message-reactions/',
          payload,
          { headers: this.getApiHeaders() }
        )
        // Sincronizar el id real devuelto por el API
        const saved = res.data
        if (saved && saved.id) {
          const target = msg.reactions.find(
            r => String(r.idUser) === userId && r.reactionType === type
          )
          if (target) this.$set(target, 'id', saved.id)
        }
      } catch (err) {
        console.error('Error al reaccionar', err)
      }
    },
    async deleteReaction (msg, reaction) {
      // Eliminación optimista
      const idx = msg.reactions.indexOf(reaction)
      if (idx !== -1) msg.reactions.splice(idx, 1)
      if (!reaction.id) return // Sin id real todavía, nada que borrar en servidor
      try {
        await axios.delete(
          `https://residencial-services.intrare.app/message-reactions/${reaction.id}/`,
          { headers: this.getApiHeaders() }
        )
      } catch (err) {
        console.error('Error al eliminar reacción', err)
        // Revertir si falla
        msg.reactions.push(reaction)
      }
    },
    openCreateChannelDialog () {
      this.isEditingChannel = false
      this.channelForm = { id: null, name: '', description: '', isClosed: true }
      this.channelDialog = true
    },
    openEditChannelDialog () {
      if (!this.selectedChannel) return
      this.isEditingChannel = true
      this.channelForm = { ...this.selectedChannel }
      this.channelDialog = true
    },
    async saveChannel () {
      if (!this.$refs.channelForm.validate()) return
      this.savingChannel = true
      try {
        if (this.isEditingChannel) {
          const res = await axios.put(`https://residencial-services.intrare.app/channels/${this.channelForm.id}/`, this.channelForm, { headers: this.getApiHeaders() })
          const index = this.channels.findIndex(c => c.id === this.channelForm.id)
          if (index !== -1) this.$set(this.channels, index, res.data)
          this.selectedChannel = res.data
          this.showNotification('Chat actualizado con éxito')
        } else {
          const res = await axios.post('https://residencial-services.intrare.app/channels/', this.channelForm, { headers: this.getApiHeaders() })
          this.channels.unshift(res.data)
          this.showNotification('Chat creado con éxito')
        }
        this.channelDialog = false
      } catch (err) {
        console.error('Error saving channel', err)
        // Mock behavior for UI demo
        if (this.isEditingChannel) {
          const index = this.channels.findIndex(c => c.id === this.channelForm.id)
          if (index !== -1) this.$set(this.channels, index, { ...this.channelForm, updatedAt: new Date().toISOString() })
          this.selectedChannel = this.channels[index]
        } else {
          const newChannel = { ...this.channelForm, id: Date.now().toString(), updatedAt: new Date().toISOString() }
          this.channels.unshift(newChannel)
        }
        this.showNotification('Chat guardado (Mock)')
        this.channelDialog = false
      } finally {
        this.savingChannel = false
      }
    },
    async confirmDeleteChannel () {
      if (!this.selectedChannel) return
      if (!confirm(`¿Estás seguro de eliminar el chat "${this.selectedChannel.name}"?`)) return
      try {
        await axios.delete(`https://residencial-services.intrare.app/channels/${this.selectedChannel.id}/`, { headers: this.getApiHeaders() })
        this.stopMessagePolling()
        this.channels = this.channels.filter(c => c.id !== this.selectedChannel.id)
        this.selectedChannel = null
        this.resetPagination()
        this.showNotification('Chat eliminado')
      } catch (err) {
        console.error('Error deleting channel', err)
        // Mock behavior
        this.stopMessagePolling()
        this.channels = this.channels.filter(c => c.id !== this.selectedChannel.id)
        this.selectedChannel = null
        this.resetPagination()
        this.showNotification('Chat eliminado (Mock)')
      }
    },
    // Membership status by resident (new endpoint)
    async fetchResidentsMembershipStatus (channelId) {
      this.loadingResidents = true
      try {
        const response = await axios.get(
          `https://residencial-services.intrare.app/channel-memberships/residents-membership-status/?idChannel=${channelId}`,
          { headers: this.getApiHeaders() }
        )
        this.residentsMembershipStatus = Array.isArray(response.data) ? response.data : []
      } catch (err) {
        console.error('Error fetching residents membership status', err)
        this.residentsMembershipStatus = []
        this.showNotification('No se pudo cargar el listado de residentes', 'error')
      } finally {
        this.loadingResidents = false
      }
    },
    openAddMemberDialog () {
      if (!this.selectedChannel) return
      this.selectedResidents = []
      this.searchResident = ''
      this.memberStatusFilter = 'not_member'
      this.residentsMembershipStatus = []
      this.addMemberDialog = true
      this.fetchResidentsMembershipStatus(this.selectedChannel.id)
    },
    toggleResident (resident) {
      if (!this.isResidentSelectable(resident)) return
      const idx = this.selectedResidents.indexOf(resident.residentId)
      if (idx === -1) {
        this.selectedResidents.push(resident.residentId)
      } else {
        this.selectedResidents.splice(idx, 1)
      }
    },
    removeSelectedResident (residentId) {
      this.selectedResidents = this.selectedResidents.filter(id => id !== residentId)
    },
    isResidentSelectable (resident) {
      // Solo se pueden invitar residentes que aún no son miembros o que rechazaron
      return resident.membershipStatus === 'not_member' || resident.membershipStatus === 'rejected'
    },
    getResidentName (residentId) {
      const r = this.residentsMembershipStatus.find(x => x.residentId === residentId)
      if (!r) return residentId
      return `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Residente'
    },
    getResidentColor (name) {
      if (!name) return this.avatarColors[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      return this.avatarColors[Math.abs(hash) % this.avatarColors.length]
    },
    getMembershipStatusLabel (status) {
      const labels = {
        accepted: 'Miembro',
        pending: 'Pendiente',
        rejected: 'Rechazado',
        not_member: 'No invitado'
      }
      return labels[status] || status
    },
    getMembershipStatusColor (status) {
      const colors = {
        accepted: 'green lighten-4',
        pending: 'orange lighten-4',
        rejected: 'red lighten-4',
        not_member: 'grey lighten-3'
      }
      return colors[status] || 'grey lighten-3'
    },
    getMembershipStatusTextColor (status) {
      const colors = {
        accepted: 'green darken-3',
        pending: 'orange darken-3',
        rejected: 'red darken-3',
        not_member: 'grey darken-2'
      }
      return colors[status] || 'grey darken-2'
    },
    getMembershipStatusIcon (status) {
      const icons = {
        accepted: 'mdi-check-circle',
        pending: 'mdi-clock-outline',
        rejected: 'mdi-close-circle',
        not_member: 'mdi-account-plus-outline'
      }
      return icons[status] || 'mdi-help-circle-outline'
    },
    async addMember () {
      if (!this.selectedResidents || this.selectedResidents.length === 0 || !this.selectedChannel) return
      this.addingMember = true
      try {
        const payload = {
          idChannel: this.selectedChannel.id,
          resident_ids: this.selectedResidents
        }
        await axios.post('https://residencial-services.intrare.app/channel-memberships/bulk-add/', payload, { headers: this.getApiHeaders() })
        this.showNotification('Residentes invitados al chat correctamente')
        this.addMemberDialog = false
      } catch (err) {
        console.error('Error adding members', err)
        this.showNotification('Error al invitar residentes', 'error')
      } finally {
        this.addingMember = false
      }
    }
  }
}
</script>

<style scoped>
.whatsapp-container {
  height: calc(100vh - 88px); /* app-bar 64px + padding del contenedor */
  max-height: calc(100vh - 88px);
  width: 100%;
  max-width: none;
  overflow: hidden;
  background-color: #f0f2f5;
  box-sizing: border-box;
  border-radius: 0;
}

.whatsapp-panels {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 34%;
  max-width: 420px;
  min-width: 280px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: #ffffff;
  border-right: 1px solid #d1d7db;
  flex-shrink: 0;
}

.sidebar-top {
  flex-shrink: 0;
  background-color: #ffffff;
}

.sidebar-header,
.chat-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e9edef;
}

.sidebar-title {
  font-size: 1rem !important;
}

.sidebar-search {
  background-color: #ffffff;
}

.search-input {
  border-radius: 8px;
}
.search-input .v-input__slot {
  background-color: #f0f2f5 !important;
}

.channel-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.channel-list {
  background: transparent;
}

.channel-item {
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
}
.channel-item:hover {
  background-color: #f5f6f6;
}
.active-channel {
  background-color: #f0f2f5;
}

.channel-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.chat-area {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background-color: #efeae2;
  background-image:
    linear-gradient(rgba(229, 221, 213, 0.85), rgba(229, 221, 213, 0.85));
}

.chat-top {
  flex-shrink: 0;
  z-index: 2;
}

.chat-header-title {
  min-width: 0;
  overflow: hidden;
  line-height: 1.2;
}

.chat-header-desc {
  max-width: min(400px, 40vw);
}

.chat-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 5% 20px;
  -webkit-overflow-scrolling: touch;
}

.chat-input-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  background-color: #f0f2f5;
  border-top: 1px solid #d1d7db;
  z-index: 2;
}

.send-btn {
  flex-shrink: 0;
}

.message-date-group {
  width: 100%;
}

.date-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 12px 0 16px;
  position: sticky;
  top: 4px;
  z-index: 5;
}

.date-separator-label {
  background-color: rgba(255, 255, 255, 0.95);
  color: #54656f;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 14px;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
  letter-spacing: 0.2px;
}

.message-wrapper {
  width: 100%;
}

.message-card {
  max-width: 65%;
  border-radius: 8px;
  position: relative;
  word-wrap: break-word;
}

.sender-info {
  min-width: 0;
}

.sender-address-chip {
  height: 16px !important;
  font-size: 9px !important;
  max-width: 160px;
}

.message-mine {
  background-color: #d9fdd3 !important;
  border-top-right-radius: 0;
}

.message-admin {
  background-color: #ede7f6 !important;
  border-top-left-radius: 0;
  border-left: 3px solid #7e57c2;
}

.message-resident {
  background-color: #ffffff !important;
  border-top-left-radius: 0;
  border-left: 3px solid #26a69a;
}

.delete-icon {
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.message-card:hover .delete-icon,
.message-card:focus-within .delete-icon {
  opacity: 1;
}

.empty-chat-state {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  background-color: #f0f2f5;
  border-bottom: 6px solid #25d366;
  overflow: hidden;
}

.empty-chat-title {
  text-align: center;
  padding: 0 16px;
}

.bg-white-transparent {
  background-color: rgba(255, 255, 255, 0.9);
}

.channel-list-scroll::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar {
  width: 6px;
}
.channel-list-scroll::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}
.channel-list-scroll::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.channel-list-scroll::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

@media (max-width: 1263px) {
  .sidebar {
    width: 38%;
    max-width: 360px;
    min-width: 260px;
  }

  .message-card {
    max-width: 75%;
  }

  .chat-messages {
    padding: 14px 4% 16px;
  }
}

@media (max-width: 959px) {
  .whatsapp-container {
    height: calc(100vh - 72px); /* app-bar móvil ~56px + padding */
    max-height: calc(100vh - 72px);
  }

  .whatsapp-panels {
    flex-direction: column;
  }

  .sidebar,
  .chat-area {
    width: 100%;
    max-width: none;
    min-width: 0;
    height: 100%;
  }

  .chat-messages {
    padding: 12px 10px 16px;
  }

  .message-card {
    max-width: 88%;
  }

  .chat-header-desc {
    max-width: 180px;
  }

  .sender-address-chip {
    max-width: 120px;
  }

  .delete-icon {
    opacity: 0.7;
  }

  .chat-input-bar {
    padding: 8px 6px;
  }

  .message-input-field {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .whatsapp-container {
    height: calc(100vh - 72px);
    max-height: calc(100vh - 72px);
  }

  .message-card {
    max-width: 92%;
  }

  .chat-header-desc {
    max-width: 120px;
  }

  .date-separator-label {
    font-size: 11px;
    padding: 3px 10px;
  }

  .empty-chat-state .v-icon {
    font-size: 80px !important;
  }

  .sidebar {
    min-width: 0;
  }
}
/* ========== Member Dialog Styles ========== */
.member-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.member-dialog-header {
  background-color: #075e54;
  padding: 12px 8px 8px;
  flex-shrink: 0;
}

.member-dialog-header-top {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.member-dialog-header-top .v-btn {
  color: #ffffff !important;
}

.member-dialog-title-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.member-dialog-title {
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.2;
}

.member-dialog-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-dialog-search {
  padding: 0 4px 6px;
}

.member-search-input .v-input__slot {
  background-color: #ffffff !important;
  border-radius: 20px !important;
}

.member-search-input .v-input__control .v-input__slot fieldset {
  border: none !important;
}

.member-selected-chips {
  display: flex;
  flex-wrap: wrap;
  padding: 4px 4px 2px;
  gap: 2px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-top: 4px;
}

/* Filtros de estado */
.member-filter-tabs {
  display: flex;
  overflow-x: auto;
  background-color: #ffffff;
  border-bottom: 1px solid #e9edef;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.member-filter-tabs::-webkit-scrollbar {
  display: none;
}

.member-filter-btn {
  flex-shrink: 0;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #54656f;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;
  outline: none;
}

.member-filter-btn:hover {
  color: #075e54;
  background-color: #f5f6f6;
}

.member-filter-btn--active {
  color: #075e54;
  border-bottom-color: #075e54;
  font-weight: 600;
}

/* Lista de residentes */
.member-list-container {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 200px;
  max-height: 380px;
  background-color: #ffffff;
  -webkit-overflow-scrolling: touch;
}

.member-loading,
.member-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.member-list {
  display: flex;
  flex-direction: column;
}

.member-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f2f2f2;
  transition: background-color 0.15s;
  gap: 12px;
  user-select: none;
}

.member-item:hover:not(.member-item--disabled) {
  background-color: #f5f6f6;
}

.member-item--selected {
  background-color: #e8f5e9;
}

.member-item--disabled {
  opacity: 0.55;
  cursor: default;
}

/* Avatar */
.member-item-avatar {
  position: relative;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
}

.member-avatar-img {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  object-fit: cover;
}

.member-avatar-fallback {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

.member-avatar-check {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background-color: rgba(7, 94, 84, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Info del residente */
.member-item-info {
  flex: 1;
  min-width: 0;
}

.member-item-name {
  font-size: 14px;
  font-weight: 600;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.member-item-house {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #8696a0;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badge de estado */
.member-item-status {
  flex-shrink: 0;
}

.membership-badge {
  font-size: 10px !important;
  height: 20px !important;
}

/* Footer del diálogo */
.member-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background-color: #f0f2f5;
  border-top: 1px solid #d1d7db;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.member-footer-info {
  flex: 1;
  min-width: 0;
}

.member-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.member-add-btn {
  text-transform: none;
  font-weight: 600;
}

/* Responsive del diálogo de miembros */
@media (max-width: 600px) {
  .member-dialog-card {
    max-height: 95vh;
    border-radius: 12px !important;
  }

  .member-list-container {
    max-height: 300px;
  }

  .member-dialog-title {
    font-size: 0.9rem;
  }

  .member-filter-btn {
    padding: 8px 10px;
    font-size: 12px;
  }

  .member-item {
    padding: 8px 12px;
    gap: 10px;
  }

  .member-item-avatar,
  .member-avatar-img,
  .member-avatar-fallback {
    width: 40px;
    height: 40px;
  }

  .member-item-name {
    font-size: 13px;
  }

  .member-dialog-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 12px;
  }

  .member-footer-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* ========== Reaction System ========== */

/* Contenedor relativo del mensaje */
.message-outer {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  max-width: 65%;
}

.message-outer--mine {
  align-items: flex-end;
}

.message-outer--other {
  align-items: flex-start;
}

/* El message-card dentro del outer ya no necesita max-width propio */
.message-outer .message-card {
  max-width: 100%;
  width: 100%;
}

.reaction-picker-btn {
  font-size: 20px;
  line-height: 1;
  padding: 3px 4px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s, background-color 0.15s;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.reaction-picker-btn:hover {
  transform: scale(1.3);
  background-color: #f0f2f5;
}

.reaction-picker-btn--active {
  background-color: #e3f2fd;
  transform: scale(1.15);
}

/* Badges de reacciones bajo el contenido */
.reaction-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}

.reaction-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.85);
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  padding: 2px 7px 2px 5px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, transform 0.1s;
  outline: none;
  line-height: 1.3;
  user-select: none;
}

.reaction-badge:hover {
  background-color: #f0f0f0;
  transform: scale(1.06);
}

.reaction-badge:active {
  transform: scale(0.97);
}

/* Reacción propia: resaltada con color primario */
.reaction-badge--mine {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.reaction-badge-emoji {
  font-size: 15px;
  line-height: 1;
}

.reaction-badge-count {
  font-size: 11px;
  font-weight: 600;
  color: #54656f;
}

.reaction-badge--mine .reaction-badge-count {
  color: #1565c0;
}

/* Responsive: ajustar max-width del outer igual que message-card */
@media (max-width: 1263px) {
  .message-outer {
    max-width: 75%;
  }
}

@media (max-width: 959px) {
  .message-outer {
    max-width: 88%;
  }

  .reaction-badges {
    margin-top: 4px;
  }

  .reaction-badge {
    padding: 3px 8px 3px 6px;
  }

  .reaction-picker-btn {
    font-size: 18px;
    padding: 2px 3px;
  }
}

@media (max-width: 600px) {
  .message-outer {
    max-width: 92%;
  }

  .reaction-badge {
    font-size: 12px;
    padding: 2px 6px 2px 4px;
  }

  .reaction-badge-emoji {
    font-size: 14px;
  }
}
</style>
